/**
 * Pure queue-reorder math shared by the drag-to-reorder gesture and the
 * arrow-button fallback on the operator queue board (`routes/queue.tsx`).
 *
 * The In-Queue column is rendered as a 0-indexed array sorted ascending by
 * `position`. The `reorderQueue` Cloud Function accepts a 1-based `toPosition`
 * (clamped server-side to [1, N]) and, after the move, the ticket's final
 * 1-based index equals that `toPosition` — verified against
 * apps/functions/src/queue/reorderQueue.ts (it splices the row out, re-inserts
 * at `toPosition - 1`, then renumbers 1..N). So the position to send is simply
 * the ticket's destination 0-based index + 1.
 *
 * `computeToPosition` maps a "move the row from index A to index B" intent to
 * the 1-based `toPosition` to send. It clamps B into [0, length-1] (mirroring
 * the server clamp) and returns `null` when the move is a no-op, so the caller
 * can skip the round-trip entirely and avoid a redundant audit row.
 */
export function computeToPosition(
  fromIndex: number,
  toIndex: number,
  length: number,
): number | null {
  if (length <= 0) return null;
  const target = Math.max(0, Math.min(length - 1, toIndex));
  if (target === fromIndex) return null;
  return target + 1;
}
